import queryString from "query-string";
import { HttpMethod } from "shared/api/http";
import { pillarApiErrorFromResponse } from "shared/api/PillarApiError";
import { PaginationRequest } from "shared/api/types/pagination";
import { FiltersRequest } from "shared/filter-where-clause";
import { SessionView } from "shared/mappers/database/session/session";
export const RequestHeader = {
  defaultJSON: { "Content-Type": "application/json" },
};

export interface ApiResponse<T = any> {
  body?: T;
  message?: string;
  status: number;
  isSuccessful: boolean;
  serverSideContext?: SessionView;
  error?: string;
}

export const addFiltersAndPaginationToUrl = (
  url: string,
  filters: FiltersRequest,
  paginationRequest?: PaginationRequest,
) => {
  const stringified = queryString.stringifyUrl(
    {
      url: url,
      query: { ...filters, ...paginationRequest },
    },
    {
      skipNull: true,
      skipEmptyString: true,
      arrayFormat: "bracket",
    },
  );
  return stringified;
};
/**
 * Reads a failed response as the JSON body `handleError` writes.
 *
 * A body that is not JSON at all - a proxy timeout page, an empty response -
 * reads as no body rather than throwing a parse error over the real failure.
 * The read happens on a clone so the untouched response is still there for
 * anyone debugging in the network tab.
 */
const readErrorBody = async (
  response: Response,
): Promise<ApiResponse | undefined> => {
  try {
    return (await response.clone().json()) as ApiResponse;
  } catch {
    return undefined;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ApiRequest = async <T = any>(
  route: string,
  requestMethod: HttpMethod,
  requestBody?: object | string | number,
  customHeaders?: HeadersInit,
  customParser?: (Response: Response) => ApiResponse["body"],
  filters?: FiltersRequest,
  pagination?: PaginationRequest,
  //This was needed to support legacy code but still allow for the return of the axios response
  //In several places, our code needs to know the status or other meta of the response, not just the data.
  returnRawResponse = false,
): Promise<ApiResponse<T> | Response | null> => {
  let response;

  const isPillarAdmin = !process.env.REACT_APP_PILLAR_API_URL;

  if (filters || pagination) {
    route = addFiltersAndPaginationToUrl(route, filters ?? {}, pagination);
  }
  if (requestMethod === HttpMethod.GET) {
    response = await fetch(route, {
      credentials: "include",
      ...(isPillarAdmin ? { next: { revalidate: 0 } } : {}),
      cache: "no-store",
      redirect: "follow",
      headers: { ...(isPillarAdmin ? { "pillar-admin": "true" } : {}) },
    });
  } else {
    response = await fetch(route, {
      credentials: "include",
      method: requestMethod,
      headers: {
        ...(customHeaders != undefined && customHeaders != null
          ? customHeaders
          : RequestHeader.defaultJSON),
        ...(isPillarAdmin ? { "pillar-admin": "true" } : {}),
      },
      redirect: "follow",
      body: JSON.stringify(requestBody),
      ...(isPillarAdmin ? { next: { revalidate: 0 } } : {}),
      cache: "no-store",
    });
  }
  if (response.status === 204) {
    return null;
  }

  if (response.status >= 500 && response.status <= 599) {
    throw pillarApiErrorFromResponse({
      statusCode: response.status,
      body: await readErrorBody(response),
      fallbackMessage: "Server Error",
    });
  }

  // 403/401 read the body like every other failure branch does. The server
  // sends `debug` (the scope check that rejected, e.g. "Scope" vs "ProfileId")
  // and, outside prod, the session it decoded - without it the caller only ever
  // sees "Forbidden" and has no way to tell an auth bug from a permission one.
  if (response.status === 403) {
    const error = pillarApiErrorFromResponse({
      statusCode: 403,
      body: await readErrorBody(response),
      fallbackMessage: "Forbidden",
    });
    window.location.href = "/forbidden?redirectUrl=" + window.location.pathname;
    throw error;
  }
  if (response.status === 401) {
    const error = pillarApiErrorFromResponse({
      statusCode: 401,
      body: await readErrorBody(response),
      fallbackMessage: "Unauthorized",
    });
    window.location.href = "/login?redirectUrl=" + window.location.pathname;
    throw error;
  }

  if (response.status >= 400 && response.status <= 499) {
    const errorBody = await readErrorBody(response);
    // An error status whose envelope still says isSuccessful:true is a
    // deliberate answer rather than a failure - a 409 saying "that user name is
    // taken", say - so its body falls through to the caller instead of being
    // thrown.  readErrorBody reads a clone, so the body is still unread here.
    if (errorBody?.isSuccessful !== true) {
      throw pillarApiErrorFromResponse({
        statusCode: response.status,
        body: errorBody,
        fallbackMessage: response.statusText,
      });
    }
  }

  return returnRawResponse
    ? response
    : customParser == undefined
      ? await response.json()
      : await customParser(response);
};
export default ApiRequest;
